import type {
  DocumentData,
  DocumentReference,
  Unsubscribe,
} from 'firebase/firestore'
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'

import type { ProjectIdentity, SessionListContent } from '@store/types'

import firestore from './firebase-db'

const MASTER_KEY_COLLECTION = 'master-key'

interface MasterKeyDocumentData {
  activeProject?: string
  id?: string
}

interface ResolveMasterKeyDocResult {
  ref: DocumentReference<DocumentData>
  data: MasterKeyDocumentData
}

interface ActiveProjectIdentityPatchParams {
  activeProject: string
  currentProject: string
  sessionList: SessionListContent[]
}

const resolveMasterKeyDoc = async (
  masterKey: string
): Promise<ResolveMasterKeyDocResult | null> => {
  if (!firestore || !masterKey) {
    return null
  }

  try {
    const docRef = doc(firestore, MASTER_KEY_COLLECTION, masterKey)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    return {
      ref: docRef,
      data: docSnap.data() as MasterKeyDocumentData,
    }
  } catch (error) {
    console.error(
      `resolveMasterKeyDoc: Failed to fetch master key document for '${masterKey}':`,
      error
    )
    return null
  }
}

export const resolveActiveProjectIdentityPatch = ({
  activeProject,
  currentProject,
  sessionList,
}: ActiveProjectIdentityPatchParams): Partial<ProjectIdentity> => {
  if (!activeProject || !currentProject || activeProject === currentProject) {
    return {}
  }

  const activeSession = sessionList.find(
    (session) => session.projectId === activeProject
  )

  if (!activeSession) {
    return {}
  }

  return {
    projectId: activeSession.projectId,
    projectName: activeSession.projectId,
    projectUUID: activeSession.projectUUID,
    sessionId: activeSession.sessionId,
    projectLabel: activeSession.projectName,
  }
}

export const updateMasterKeyActiveProject = async ({
  masterKey,
  activeProject,
}: {
  masterKey: string
  activeProject: string
}): Promise<boolean> => {
  if (!activeProject) {
    return false
  }

  const resolvedMasterKeyDoc = await resolveMasterKeyDoc(masterKey)

  if (!resolvedMasterKeyDoc) {
    return false
  }

  try {
    await updateDoc(resolvedMasterKeyDoc.ref, {
      activeProject,
    })
    return true
  } catch (error) {
    console.error(
      `updateMasterKeyActiveProject: Failed to update active project for master key '${masterKey}' to '${activeProject}':`,
      error
    )
    return false
  }
}

export const getMasterKeyActiveProject = async (
  masterKey: string
): Promise<string> => {
  const resolvedMasterKeyDoc = await resolveMasterKeyDoc(masterKey)

  if (!resolvedMasterKeyDoc) {
    return ''
  }

  return resolvedMasterKeyDoc.data.activeProject || ''
}

export const subscribeToMasterKeyActiveProject = async (
  masterKey: string,
  onChange: (activeProject: string) => void | Promise<void>
): Promise<Unsubscribe | undefined> => {
  const resolvedMasterKeyDoc = await resolveMasterKeyDoc(masterKey)

  if (!resolvedMasterKeyDoc) {
    return undefined
  }

  return onSnapshot(
    resolvedMasterKeyDoc.ref,
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange('')
        return
      }

      const data = snapshot.data() as MasterKeyDocumentData
      onChange(data?.activeProject || '')
    },
    (error) => {
      console.error(
        `subscribeToMasterKeyActiveProject: Failed to subscribe to master key '${masterKey}' changes:`,
        error
      )
    }
  )
}
